import * as React from 'react'
import Icon from '../Icons/Icon'
import ExpenseTicket from './ExpenseTicket'
import { ExpensesController } from '../../controllers'
import Notification from '../../utilities/Notification'
import Loader from '../Loaders/Loader'
import ExpenseTicketPlaceholder from './ExpenseTicketPlaceholder'
import { Trans, Translation } from 'react-i18next'
import ExpenseTicketDropzone from './ExpenseTicketDropzone'
import { Expense, MimeTypes, Settings, WorkspaceChannelEvent, WorkspaceChannelEventType } from '../../types'
import ActionCableConsumer from '../../consumers/ActionCableConsumer'
import styled from 'styled-components'

const Title = styled.h3`
  font-weight: bold;
`

interface IProps {
  onAddClick: (e: React.MouseEvent<HTMLDivElement>) => void
  onExpenseClick: (expense: Expense) => void
  onReviewClick: (expense: Expense) => void
  onDeleteClick: (expense: Expense) => void
  setting: Settings
  allowedFileTypes: MimeTypes[]
  onFilesDropped: (files: any[]) => void
  workspaceId: string
}

interface IState {
  expenses: Expense[]
  currentPage: number
  reachedEnd: boolean
  isLoading: boolean
  isUploading: boolean
}

export default class ExpensesLastUpdated extends React.Component<IProps, IState> {
  private container = React.createRef<HTMLDivElement>()

  constructor(props: IProps) {
    super(props)

    this.state = {
      expenses: [],
      currentPage: 1,
      reachedEnd: false,
      isLoading: false,
      isUploading: false,
    }

    this.onDrop = this.onDrop.bind(this)
    this.onListScroll = this.onListScroll.bind(this)
    this.onActionCableConnected = this.onActionCableConnected.bind(this)
    this.onActionCableDisconnected = this.onActionCableDisconnected.bind(this)
    this.onActionCableReceived = this.onActionCableReceived.bind(this)
  }

  componentDidMount() {
    this.fetchUnreviewedExpenses(1)

    if (this.container.current) this.container.current.addEventListener('scroll', this.onListScroll)
  }

  componentWillUnmount() {
    if (this.container.current) this.container.current.removeEventListener('scroll', this.onListScroll)
  }

  setUploading(isUploading) {
    this.setState({
      isUploading: isUploading,
    })
  }

  processFiles(files) {
    return Object.values(files)
  }

  onDrop(acceptedFiles) {
    const { onFilesDropped } = this.props

    if (onFilesDropped) {
      onFilesDropped(acceptedFiles)
    }
  }

  onListScroll(e) {
    const { reachedEnd, isLoading } = this.state
    const container = this.container.current
    const scrollLeft = container.scrollLeft
    const scrollWidth = container.scrollWidth
    const clientWidth = container.clientWidth
    const scrollEndReached = Math.abs(scrollLeft) === scrollWidth - clientWidth

    if (scrollEndReached && !reachedEnd && !isLoading) {
      const { currentPage } = this.state
      this.fetchUnreviewedExpenses(currentPage + 1)
    }
  }

  refresh() {
    this.fetchUnreviewedExpenses(1)
  }

  async fetchUnreviewedExpenses(page = 1) {
    try {
      this.setState({ isLoading: true })

      const response = await ExpensesController.getExpenses({
        page: page,
        order: 'updated_at_desc',
        per_page: 10,
        reviewed: false
      })

      const { expenses: stateExpenses } = this.state;
      const { expenses, current_page, total_pages } = response

      this.setState({
        expenses: page > 1 ? [...stateExpenses, ...expenses] : expenses,
        reachedEnd: current_page === total_pages,
        currentPage: current_page,
        isLoading: false,
      });
    } catch (ex) {
      console.error(ex)
    }
  }

  onActionCableConnected() {
    console.log('[WorkspaceChannel] connected')
  }

  onActionCableDisconnected() {
    console.log('[WorkspaceChannel] disconnected')
  }

  onActionCableReceived(event: WorkspaceChannelEvent) {
    const { expenses } = this.state
    console.log('[WorkspaceChannel] event received', event)

    switch (event.type) {
      case WorkspaceChannelEventType.EXPENSE_CREATE:
        const existingExpenseIndex = expenses.findIndex(expense => expense.id === event.data.expense.id)

        if (event.data.expense.reviewed) {
          this.setState({ expenses: expenses.filter(expense => expense.id !== event.data.expense.id) })
        } else if (existingExpenseIndex === -1) {
          this.setState({ expenses: [event.data.expense, ...expenses] })
        } else {
          const updatedExpenses = [...expenses]

          updatedExpenses[existingExpenseIndex] = event.data.expense

          this.setState({ expenses: updatedExpenses })
        }
        break
      case WorkspaceChannelEventType.EXPENSE_UPDATE:
        const expenseIndex = expenses.findIndex(expense => expense.id === event.data.expense.id)

        if (event.data.expense.reviewed) {
          this.setState({ expenses: expenses.filter(expense => expense.id !== event.data.expense.id) })
        } else if (expenseIndex === -1) {
          this.setState({ expenses: [event.data.expense, ...expenses] })
        } else {
          const updatedExpenses = [...expenses]

          updatedExpenses[expenseIndex] = event.data.expense

          this.setState({ expenses: updatedExpenses })
        }
        break
      case WorkspaceChannelEventType.EXPENSE_DELETE:
        this.setState({
          expenses: expenses.filter(expense => expense.id !== event.data.expense_id)
        })
        break
    }
  }

  render() {
    const {
      setting,
      onAddClick,
      onExpenseClick,
      onReviewClick,
      onDeleteClick,
      workspaceId,
    } = this.props
    const { expenses, isLoading, isUploading } = this.state

    return (
      <ActionCableConsumer
        channel={{ channel: 'WorkspaceChannel', id: workspaceId }}
        onConnected={this.onActionCableConnected}
        onDisconnected={this.onActionCableDisconnected}
        onReceived={this.onActionCableReceived}
      >
        <Translation>
          {
            (t, { i18n }) => <>
              <Title>{t('ExpensesReview::Review')}</Title>
              <div ref={this.container} className='expense-tickets'>
                <ExpenseTicketDropzone
                  uploading={isUploading}
                  onDrop={this.onDrop}
                />

                {(expenses.length === 0 || (expenses.length === 0 && isLoading)) && [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(placeholder => {
                  return (
                    <ExpenseTicketPlaceholder
                      key={placeholder}
                    />
                  )
                })}

                {expenses.map(expense => {
                  return (
                    <ExpenseTicket
                      key={expense.id}
                      expense={expense}
                      setting={setting}
                      onClick={onExpenseClick}
                      onReviewClick={onReviewClick}
                      onDeleteClick={onDeleteClick}
                    />
                  )
                })}
              </div>
            </>
          }
        </Translation>
      </ActionCableConsumer>
    )
  }
}